<template>
  <div>
    <Pane />
    <a-card class="container">
      <div class="header-wrapper">
        <a-row type="flex" justify="space-between" align="middle">
          <a-col :span="18">
            <a-space>
              <a-button-group>
                <a-button icon="left" @click="prevMonth" />
                <a-month-picker
                  v-model="selectedMonth"
                  placeholder="选择年月"
                  @change="onMonthChange"
                  style="width: 150px"
                />
                <a-button icon="right" @click="nextMonth" />
              </a-button-group>
              <a-select
                v-model="selectedWeek"
                placeholder="选择周"
                style="width: 130px"
                @change="onWeekChange"
              >
                <a-select-option v-for="item in weekOptions" :key="item.value" :value="item.value">
                  {{ item.label }}
                </a-select-option>
              </a-select>
              <a-select
                v-model="selectedDepartment"
                placeholder="部门"
                show-search
                :filter-option="$selectFilterOption"
                style="width: 150px"
                allowClear
                :disabled="!isAdmin"
              >
                <a-select-option
                  v-for="item in organizationList"
                  :key="item.id"
                  :value="item.id"
                >{{ item.uniqueName }}</a-select-option>
              </a-select>
              <a-button type="primary" icon="search" @click="handleQuery">查询</a-button>
              <a-button icon="reload" @click="handleReset">重置</a-button>
            </a-space>
          </a-col>
          <a-col :span="6" style="text-align: right">
            <a-space>
              <a-button type="primary" icon="edit" @click="goToFill">
                填报计划
              </a-button>
              <a-button type="primary" icon="bar-chart" @click="goToSummary" v-if="isAdmin">
                汇总统计
              </a-button>
              <a-button icon="download" @click="handleExport" :loading="exporting">
                导出Excel
              </a-button>
            </a-space>
          </a-col>
        </a-row>
      </div>

      <a-table
        bordered
        :data-source="filteredList"
        :loading="loading"
        :pagination="{
          total,
          current,
          pageSize,
          showTotal: (total) => `共 ${total} 条记录`,
        }"
        @change="onChange"
        :scroll="{ x: 'max-content' }"
        rowKey="id"
      >
        <a-table-column title="部门" data-index="deptName" width="120px" fixed="left" />
        <a-table-column title="项目编码" data-index="contractCode" width="150px" />
        <a-table-column title="项目名称" data-index="contractName" />
        <a-table-column title="回款记录" width="200px">
          <template slot-scope="text, record">
            <div v-if="record.collectionRecordList && record.collectionRecordList.length">
              <div v-for="(item, index) in record.collectionRecordList" :key="index">
                {{ item.amount }} ({{ item.date ? item.date.substring(0, 10) : '-' }})
              </div>
              <div style="border-top: 1px solid #e8e8e8; margin-top: 4px; padding-top: 4px; font-weight: bold;">
                合计: {{ getCollectionTotal(record) }} 元 ({{ record.collectionRecordList.length }}笔)
              </div>
            </div>
            <span v-else>-</span>
          </template>
        </a-table-column>
        <a-table-column title="分包商" width="200px">
          <template slot-scope="text, record">
            {{ record.subpackageName }}{{ record.subpackageCompanyName ? ' - ' + record.subpackageCompanyName : '' }}
          </template>
        </a-table-column>
        <a-table-column title="需支付分包金额(元)" data-index="payableAmount" width="160px" align="right" />
        <a-table-column title="已支付(元)" data-index="paidAmount" width="120px" align="right" />
        <a-table-column title="剩余应支付(元)" width="150px" align="right">
          <template slot-scope="text, record">
            <span style="font-weight: bold; color: #f5222d;">{{ record.remainingAmount || 0 }}</span>
          </template>
        </a-table-column>
        <a-table-column title="支付进度" data-index="paymentProgress" width="100px" align="center">
          <template slot-scope="text, record">
            <a-progress :percent="record.paymentProgress || 0" size="small" />
          </template>
        </a-table-column>
        <a-table-column title="备注" data-index="remark" width="150px" />
      </a-table>

      <!-- 日历表格 -->
      <div v-if="weekColumns.length > 0" style="margin-top: 16px">
        <a-card title="月度日历" :bordered="false" size="small">
          <table style="width: 100%; border-collapse: collapse; font-size: 12px">
            <thead>
              <tr style="background: #f5f5f5">
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周一</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周二</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周三</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周四</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%">周五</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%; color: #999">周六</th>
                <th style="padding: 8px; border: 1px solid #e8e8e8; text-align: center; width: 14.28%; color: #999">周日</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(week, weekIndex) in calendarWeeks" :key="weekIndex">
                <td 
                  v-for="(day, dayIndex) in week" 
                  :key="dayIndex"
                  :style="{
                    padding: '8px',
                    border: '1px solid #e8e8e8',
                    textAlign: 'center',
                    backgroundColor: day.isCurrentMonth ? (day.isWeekend ? '#fff9f9' : '#fff') : '#f9f9f9',
                    color: day.isCurrentMonth ? (day.isWeekend ? '#999' : '#333') : '#ccc'
                  }"
                >
                  <div v-if="day.day">
                    <div style="font-weight: bold">{{ day.day }}</div>
                    <div v-if="day.weekNumber" style="font-size: 10px; color: #1890ff">第{{ day.weekNumber }}周</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </a-card>
      </div>
    </a-card>

  </div>
</template>

<script>
import { getListAllWeek, getDeptWeekPlan, getWeeks } from "@/api/finance/subpackage-payment-plan";
import { downloadByAoa } from "@/utils/xlsx";

import organization from "@/mixins/organization";
import moment from "moment";

export default {
  mixins: [organization],

  data() {
    return {
      selectedMonth: null,
      currentMonth: "",
      selectedWeek: undefined,
      selectedDepartment: undefined,

      list: [],
      loading: false,
      exporting: false,
      total: 0,
      current: 1,
      pageSize: 10,
      isAdmin: false,
      userDeptId: "",
      userDeptName: "",
      weekColumns: [],
      calendarWeeks: [],
    };
  },

  computed: {
    filteredList() {
      if (this.selectedDepartment) {
        const dept = this.organizationList.find(item => item.id === this.selectedDepartment);
        const deptName = dept ? dept.uniqueName : '';
        return this.list.filter(
          (item) => item.deptName === deptName
        );
      }
      return this.list;
    },
    yearMonth() {
      if (this.selectedMonth) {
        return moment(this.selectedMonth).format("YYYY-MM");
      }
      return this.currentMonth;
    },
    weekOptions() {
      return this.weekColumns.map((item) => ({
        label: `第${item.weekNumber}周`,
        value: item.weekNumber,
      }));
    },
  },

  created() {
      this.initUserAndLoad();
  },

  methods: {
    initUserAndLoad() {
      const user = this.$store.state.auth?.user;
      const roles = user?.roles || [];
      console.log("roles:::", roles)
      this.checkAdminRole(roles);

      this.userDeptId = user?.employeeVo?.deptId || "";
      this.userDeptName = user?.employeeVo?.deptName || "";

      if (this.userDeptId) {
        this.selectedDepartment = this.userDeptId;
      }
      this.initCurrentMonth();
      this.generateWeekColumns();
    },

    checkAdminRole(roles) {
      if (Array.isArray(roles)) {
        this.isAdmin = roles.some(
          (item) => item.code === "fin_sub_pay_plan_admin" || item.code === "admin"
        );
      } else {
        this.isAdmin = false;
      }
    },

    initCurrentMonth() {
      const now = new Date();
      this.currentMonth = moment(now).format("YYYY-MM");
      this.selectedMonth = moment(this.currentMonth, "YYYY-MM");
      this.selectedWeek = undefined;
    },

    // 生成周列
    generateWeekColumns() {
      getWeeks(this.yearMonth)
        .then((res) => {
          if (res && Array.isArray(res)) {
            this.weekColumns = res.map((week) => ({
              weekNumber: week.weekNumber,
              title: `第${week.weekNumber}周`,
              dateRange: week.dateRange,
              startDate: week.startDate,
              endDate: week.endDate,
            }));
            if (this.weekColumns.length > 0) {
              const today = new Date();
              const currentWeek = this.weekColumns.find(w => {
                const start = new Date(w.startDate);
                const end = new Date(w.endDate);
                return today >= start && today <= end;
              });
              this.selectedWeek = currentWeek ? currentWeek.weekNumber : this.weekColumns[0].weekNumber;
            }
            this.generateCalendarWeeks();
            this.handleQuery();
          }
        })
        .catch((err) => {
          this.$message.error("获取周信息失败：" + (err.message || "未知错误"));
        });
    },

    // 生成日历表格
    generateCalendarWeeks() {
      const [year, month] = this.yearMonth.split("-").map(Number);
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0);
      
      const firstDayOfWeek = firstDay.getDay();
      
      const startDay = new Date(firstDay);
      if (firstDayOfWeek !== 1) {
        const daysToSubtract = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
        startDay.setDate(startDay.getDate() - daysToSubtract);
      }
      
      const endDay = new Date(lastDay);
      const lastDayOfWeek = lastDay.getDay();
      if (lastDayOfWeek !== 0) {
        const daysToAdd = 7 - lastDayOfWeek;
        endDay.setDate(endDay.getDate() + daysToAdd);
      }
      
      const weeks = [];
      let currentDay = new Date(startDay);
      
      while (currentDay <= endDay) {
        const week = [];
        for (let i = 0; i < 7; i++) {
          const dayOfMonth = currentDay.getDate();
          const isCurrentMonth = currentDay.getMonth() === month - 1;
          const dayOfWeek = currentDay.getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          
          let weekNumber = null;
          if (isCurrentMonth) {
            for (const weekInfo of this.weekColumns) {
              const startDate = new Date(weekInfo.startDate);
              const endDate = new Date(weekInfo.endDate);
              if (currentDay >= startDate && currentDay <= endDate) {
                weekNumber = weekInfo.weekNumber;
                break;
              }
            }
          }
          
          week.push({
            day: dayOfMonth,
            isCurrentMonth: isCurrentMonth,
            isWeekend: isWeekend,
            weekNumber: weekNumber,
          });
          
          currentDay.setDate(currentDay.getDate() + 1);
        }
        weeks.push(week);
      }
      
      this.calendarWeeks = weeks;
    },

    getList() {
      if (!this.selectedWeek) {
        this.$message.warning("请选择周");
        return;
      }
      this.loading = true;
      const [year, month] = this.yearMonth.split("-");
      const params = {
        year,
        month: parseInt(month),
        weekNumber: this.selectedWeek,
      };
      if (this.isAdmin && this.selectedDepartment) {
        params.deptId = this.selectedDepartment;
      }
      console.log("this.isAdmin::::",this.isAdmin)
      const requestApi = this.isAdmin
        ? getListAllWeek(params)
        : getDeptWeekPlan(params);

      requestApi
        .then((res) => {
          if (Array.isArray(res)) {
            this.list = res;
            this.total = res.length;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    handleQuery() {
      this.current = 1;
      this.getList();
    },

    // 导出Excel（查询当周全部数据，pageSize=1000）
    handleExport() {
      if (!this.selectedWeek) {
        this.$message.warning("请选择周");
        return;
      }
      this.exporting = true;
      const [year, month] = this.yearMonth.split("-");
      const params = {
        year,
        month: parseInt(month),
        weekNumber: this.selectedWeek,
        pageSize: 1000,
      };
      if (this.isAdmin && this.selectedDepartment) {
        params.deptId = this.selectedDepartment;
      }
      const requestApi = this.isAdmin
        ? getListAllWeek(params)
        : getDeptWeekPlan(params);

      requestApi
        .then((res) => {
          const records = Array.isArray(res) ? res : [];
          if (!records.length) {
            this.$message.warning("暂无数据可导出");
            return;
          }
          const header = [
            "部门",
            "总院/分院",
            "项目编码",
            "项目名称",
            "回款记录",
            "分包商",
            "需支付分包金额(元)",
            "已支付(元)",
            "剩余应支付(元)",
            "支付进度(%)",
            "备注",
          ];
          const data = [header];
          records.forEach((record) => {
            let collectionText = "";
            if (record.collectionRecordList && record.collectionRecordList.length) {
              const lines = record.collectionRecordList.map((item) => {
                const date = item.date ? item.date.substring(0, 10) : "-";
                return `${item.amount != null ? item.amount : ""} (${date})`;
              });
              lines.push(
                `合计: ${this.getCollectionTotal(record)} 元 (${record.collectionRecordList.length}笔)`
              );
              collectionText = lines.join("\n");
            }
            data.push([
              record.deptName || "",
              record.paymentType === "branch_institute" ? "分院" : "总院",
              record.contractCode || "",
              record.contractName || "",
              collectionText,
              `${record.subpackageName || ""}${
                record.subpackageCompanyName ? " - " + record.subpackageCompanyName : ""
              }`,
              record.payableAmount || 0,
              record.paidAmount || 0,
              record.remainingAmount || 0,
              record.paymentProgress || 0,
              record.remark || "",
            ]);
          });

          const cols = [
            { wch: 15 },
            { wch: 10 },
            { wch: 18 },
            { wch: 30 },
            { wch: 30 },
            { wch: 25 },
            { wch: 16 },
            { wch: 12 },
            { wch: 14 },
            { wch: 12 },
            { wch: 20 },
          ];

          downloadByAoa(
            data,
            `${this.yearMonth}第${this.selectedWeek}周分包付款计划`,
            null,
            cols
          );
        })
        .catch((err) => {
          this.$message.error("导出失败：" + (err.message || "未知错误"));
        })
        .finally(() => {
          this.exporting = false;
        });
    },

    handleReset() {
      const now = new Date();
      this.selectedMonth = moment(now);
      this.selectedWeek = undefined;
      if (this.isAdmin) {
        this.selectedDepartment = undefined;
      }
      this.current = 1;
      this.generateWeekColumns();
    },

    onMonthChange(date) {
      this.selectedMonth = date;
      this.current = 1;
      this.generateWeekColumns();
    },

    onWeekChange() {
      this.current = 1;
    },

    onChange(pagination) {
      this.current = pagination.current;
      this.pageSize = pagination.pageSize;
    },

    goToFill() {
      this.$router.push({
        path: "/finance/subpackage-payment-plan/fill",
        query: {
          yearMonth: this.yearMonth,
        },
      });
    },

    goToSummary() {
      this.$router.push({
        path: "/finance/subpackage-payment-plan/summary",
        query: {
          year: this.yearMonth.substring(0, 4),
        },
      });
    },

    prevMonth() {
      const [year, month] = this.yearMonth.split("-").map(Number);
      const prevMonthDate = new Date(year, month - 2, 1);
      this.selectedMonth = moment(prevMonthDate);
      this.current = 1;
      this.generateWeekColumns();
    },

    nextMonth() {
      const [year, month] = this.yearMonth.split("-").map(Number);
      const nextMonthDate = new Date(year, month, 1);
      this.selectedMonth = moment(nextMonthDate);
      this.current = 1;
      this.generateWeekColumns();
    },

    getCollectionTotal(record) {
      if (!record.collectionRecordList || !record.collectionRecordList.length) return 0;
      return record.collectionRecordList.reduce((sum, item) => sum + (item.amount || 0), 0);
    },
  },
};
</script>

<style scoped>
.container {
  padding: 16px;
}

.header-wrapper {
  padding: 16px 0;
}
</style>
